// The Studio's words in each language, and the formats that go with them.
// The languages themselves and how one is picked are src/core/lang.ts,
// re-exported here; the room's half is src/ui/lang.ts.
//
// A language is a catalog (src/core/messages/<lang>.ts): one flat table of
// whole sentences keyed by name, with `{name}` placeholders for what a
// sentence carries. English is the source; every other catalog has every key
// of it with the same placeholders (test/i18n.test.ts). Adding a language
// adds a catalog and a row in LANG_INFO, not a code path.

import { EN, type MessageKey } from "./messages/en.ts";
import { JA } from "./messages/ja.ts";
import { LANG_INFO, type Lang } from "./lang.ts";
import { DEVICE_NAMES } from "./names.ts";

export * from "./lang.ts";
export * from "./names.ts";

export type { MessageKey } from "./messages/en.ts";

const CATALOGS: Record<Lang, Record<MessageKey, string>> = { en: EN, ja: JA };

/** The catalog of a language. */
export function catalog(lang: Lang): Readonly<Record<MessageKey, string>> {
  return CATALOGS[lang];
}

export type Params = Record<string, string | number>;

/** `template` with each `{name}` replaced by `params[name]`. A placeholder with no value stays as it is. */
export function fill(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => (name in params ? String(params[name]) : whole));
}

/** The sentence `key` in `lang`, filled. A key with no sentence in `lang` takes English's. Japanese keeps each number with its counter (keepNumbers). */
export function translate(lang: Lang, key: MessageKey, params?: Params): string {
  return keepNumbers(lang, fill(CATALOGS[lang][key] ?? EN[key], params));
}

// A number, with `,` `.` `:` inside it and a range (`10〜16`), then one space,
// then a Japanese character or a unit.
export const NUMBER_THEN = /(\d(?:[\d,.:]*\d)?(?:[〜～~-]\d(?:[\d,.:]*\d)?)?) (?=[\u3005\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uff66-\uff9f]|(?:KiB|MiB|GiB|KB|MB|GB|fps|km|m)(?![A-Za-z]))/g;

/**
 * Japanese text with the space between a number and its counter or unit
 * made a no-break space (U+00A0): `7 つ`, `14,753 棟`, `10〜16 メートル`,
 * `43 MB`, `2 MiB` stay on one line. Japanese writing puts a half-width space between
 * a number and the Japanese around it, and a browser may break a line there.
 * Every other space, and text in any other language, is unchanged.
 */
export function keepNumbers(lang: Lang, text: string): string {
  return lang === "ja" ? text.replace(NUMBER_THEN, "$1\u00a0") : text;
}

/** The device names a Japanese sentence can hold, longest first: the Japanese names, the English ones, and `Android` alone. */
const NAMES_IN: Partial<Record<Lang, RegExp>> = {};
function namesIn(lang: Lang): RegExp {
  const names = [...new Set([...Object.values(DEVICE_NAMES[lang]), ...Object.values(DEVICE_NAMES.en), "Android"])].sort((a, b) => b.length - a.length);
  return (NAMES_IN[lang] ??= new RegExp(names.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"), "g"));
}

/**
 * A sentence cut into runs, each device name a run of its own (`name: true`)
 * that a page sets so it does not break inside: a Japanese line may break
 * between `ニンテンドー` and `3DS`, or at the space in `PS Vita`. In a
 * language other than Japanese the sentence is one run. Numbers are kept
 * with their counters (keepNumbers) first.
 */
export function phraseRuns(lang: Lang, text: string): Array<{ text: string; name: boolean }> {
  const kept = keepNumbers(lang, text);
  if (lang !== "ja") return kept ? [{ text: kept, name: false }] : [];
  const runs: Array<{ text: string; name: boolean }> = [];
  let at = 0;
  for (const match of kept.matchAll(namesIn(lang))) {
    if (match.index! > at) runs.push({ text: kept.slice(at, match.index), name: false });
    runs.push({ text: match[0], name: true });
    at = match.index! + match[0].length;
  }
  if (at < kept.length) runs.push({ text: kept.slice(at), name: false });
  return runs;
}

/** A `t` bound to one language: `const t = translator("ja"); t("room.back")`. */
export function translator(lang: Lang): (key: MessageKey, params?: Params) => string {
  return (key, params) => translate(lang, key, params);
}

/** The placeholders a sentence carries, sorted: `["app", "device"]`. */
export function placeholders(template: string): string[] {
  return [...new Set([...template.matchAll(/\{(\w+)\}/g)].map((match) => match[1]!))].sort();
}

/** A size in bytes as a page shows it: `840 KB`, `7.4 MB`, `149 MB`. The units read the same in both languages. */
export function formatBytes(lang: Lang, bytes: number): string {
  const number = (value: number, digits: number) => new Intl.NumberFormat(LANG_INFO[lang].tag, { maximumFractionDigits: digits }).format(value);
  if (bytes < 100_000) return keepNumbers(lang, `${number(Math.max(1, Math.round(bytes / 1000)), 0)} KB`);
  if (bytes < 10_000_000) return keepNumbers(lang, `${number(Math.round(bytes / 100_000) / 10, 1)} MB`);
  return keepNumbers(lang, `${number(Math.round(bytes / 1_000_000), 0)} MB`);
}

/** A day as a page shows it: `Oct 7, 2026`, `2026年10月7日`. */
export function formatDay(lang: Lang, time: number): string {
  return new Intl.DateTimeFormat(LANG_INFO[lang].tag, lang === "ja"
    ? { year: "numeric", month: "long", day: "numeric" }
    : { day: "numeric", month: "short", year: "numeric" }).format(time);
}

/** Items as one phrase: `PSP, PS Vita and Nintendo 3DS`; in Japanese `PSP、PS Vita、ニンテンドー3DS`. */
export function formatList(lang: Lang, items: readonly string[]): string {
  if (lang === "ja") return keepNumbers(lang, items.join("、"));
  if (items.length < 2) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * An amount as a price: US cents as `$7.99`, yen as `¥1,280`. The membership
 * is priced in dollars, and in yen for a buyer in Japan; a checkout elsewhere
 * shows the dollar amount in the buyer's own currency.
 */
export function formatPrice(_lang: Lang, amount: number, currency: "usd" | "jpy" = "usd"): string {
  if (currency === "jpy") return `¥${Math.round(amount).toLocaleString("en-US")}`;
  return `$${(amount / 100).toFixed(2)}`;
}

